import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy, Link2 } from "lucide-react";
import { toast } from "sonner";

/**
 * ShareBar — one-tap share row for a student's live campaign.
 *
 * Renders X, WhatsApp, Instagram, and Copy-Link buttons. Instagram
 * doesn't expose a web share URL that pre-fills a caption, so tapping
 * IG copies the caption + link to clipboard AND deep-links via
 * `instagram://` — the OS picks it up on mobile, desktop falls back
 * to the toast telling the aunt to paste into Instagram herself.
 *
 * Props:
 *   url        — link the buttons hand out (the /api/s/{slug} share card:
 *                bots see the student's photo + headline, people are redirected)
 *   displayUrl?— the short vanity address shown in the link field (default: url)
 *   studentFirstName
 *   campaignTitle
 *   goalCents?      — used in the caption when present
 *   variant?        — "light" (default, on sand) | "dark" (on ink)
 *   testidPrefix?
 */
export const ShareBar = ({
  url,
  displayUrl,
  studentFirstName,
  campaignTitle,
  goalCents,
  variant = "light",
  testidPrefix = "share",
}) => {
  const [copied, setCopied] = useState(false);

  const goalStr = goalCents
    ? ` toward a ${`$${Math.round(goalCents / 100).toLocaleString()}`} goal`
    : "";
  const caption = campaignTitle
    ? `${studentFirstName} is raising${goalStr} — "${campaignTitle}". Gifts go to My Rich Aunt, a 501(c)(3), and fund grants for verified students. Chip in or share:`
    : `${studentFirstName} just launched a campaign. Gifts go to My Rich Aunt, a 501(c)(3), and fund grants for verified students. Chip in or share:`;

  const openWindow = (target) => {
    if (typeof window === "undefined") return;
    window.open(target, "_blank", "noopener,noreferrer,width=640,height=680");
  };

  const doCopy = async () => {
    if (typeof navigator === "undefined" || !navigator.clipboard) {
      toast.error("Your browser blocked the copy — long-press the link to share it manually.");
      return;
    }
    try {
      await navigator.clipboard.writeText(`${caption}\n\n${url}`);
      setCopied(true);
      toast.success("Link copied", {
        description: `Paste it anywhere — DM, group text, story. It unfurls with ${studentFirstName}'s photo and headline.`,
      });
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Copy didn't go through. Try again?");
    }
  };

  const onX = () => {
    const q = new URLSearchParams({ text: caption, url }).toString();
    openWindow(`https://twitter.com/intent/tweet?${q}`);
  };

  const onWhatsApp = () => {
    const q = new URLSearchParams({ text: `${caption} ${url}` }).toString();
    openWindow(`https://wa.me/?${q}`);
  };

  const onInstagram = async () => {
    // IG has no compose-link that pre-fills. Best pattern: copy the
    // caption + link, then deep-link into IG. On mobile the OS opens
    // the app; on desktop the deep-link is a no-op and the toast
    // steers the user to paste into a story.
    try {
      if (navigator?.clipboard) {
        await navigator.clipboard.writeText(`${caption}\n\n${url}`);
      }
    } catch {
      /* clipboard blocked — the toast still tells them what to do */
    }
    toast.success("Caption copied for Instagram", {
      description: "Paste it into a story, post, or DM. Direct linking coming with IG stories in the app.",
    });
    if (typeof window !== "undefined") {
      const ua = navigator.userAgent || "";
      const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);
      if (isMobile) window.location.href = "instagram://";
    }
  };

  const btnClass = variant === "dark"
    ? "flex h-10 w-10 items-center justify-center rounded-full border border-sand/20 bg-sand/5 text-sand transition-colors hover:bg-sand/12"
    : "flex h-10 w-10 items-center justify-center rounded-full border border-border bg-bone text-ink transition-colors hover:border-ink hover:bg-sand";

  const linkTextClass = variant === "dark" ? "text-sand/60" : "text-olive";
  const linkFieldClass = variant === "dark"
    ? "flex items-center gap-2 rounded-full border border-sand/15 bg-sand/5 px-3 py-1.5 text-[13px]"
    : "flex items-center gap-2 rounded-full border border-border bg-bone px-3 py-1.5 text-[13px]";

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid={`${testidPrefix}-bar`}>
      <button
        type="button"
        onClick={onX}
        aria-label="Share on X"
        title="Share on X"
        data-testid={`${testidPrefix}-x`}
        className={btnClass}
      >
        <XLogo />
      </button>
      <button
        type="button"
        onClick={onWhatsApp}
        aria-label="Share on WhatsApp"
        title="Share on WhatsApp"
        data-testid={`${testidPrefix}-whatsapp`}
        className={btnClass}
      >
        <WhatsAppLogo />
      </button>
      <button
        type="button"
        onClick={onInstagram}
        aria-label="Share to Instagram"
        title="Share to Instagram"
        data-testid={`${testidPrefix}-instagram`}
        className={btnClass}
      >
        <InstagramLogo />
      </button>

      <span className={`${linkFieldClass} min-w-0`} data-testid={`${testidPrefix}-link-preview`} title={url}>
        <Link2 className="h-3 w-3 shrink-0" strokeWidth={2} />
        <span className={`truncate ${linkTextClass}`}>{shortLink(displayUrl || url)}</span>
      </span>

      <button
        type="button"
        onClick={doCopy}
        data-testid={`${testidPrefix}-copy`}
        className={
          variant === "dark"
            ? "inline-flex items-center gap-1.5 rounded-full bg-yellow px-4 py-2 text-[13px] font-semibold text-ink transition-colors hover:brightness-95"
            : "inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-[13px] font-semibold text-sand transition-colors hover:bg-forest"
        }
      >
        <motion.span
          key={copied ? "on" : "off"}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          className="inline-flex items-center gap-1.5"
        >
          {copied ? (
            <><Check className="h-3.5 w-3.5" strokeWidth={2} /> Copied</>
          ) : (
            <><Copy className="h-3.5 w-3.5" strokeWidth={2} /> Copy link</>
          )}
        </motion.span>
      </button>
    </div>
  );
};

const shortLink = (url) => {
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname}`;
  } catch {
    return url;
  }
};

// ─── Brand-official monochrome logos ────────────────────────────────
export const XLogo = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden>
    <path d="M18.244 2H21.5l-7.485 8.556L23 22h-6.812l-5.31-6.94L4.8 22H1.545l8.026-9.176L1 2h6.994l4.79 6.35L18.244 2Zm-1.19 18h1.826L7.026 4H5.09l11.964 16Z" />
  </svg>
);

export const WhatsAppLogo = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden>
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.97L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.9-4.44 9.9-9.9C21.95 6.45 17.5 2 12.04 2Zm0 18.11h-.01a8.16 8.16 0 0 1-4.15-1.14l-.3-.18-3.09.81.83-3.02-.19-.31a8.16 8.16 0 0 1-1.25-4.36c0-4.52 3.68-8.2 8.2-8.2 2.19 0 4.24.85 5.79 2.4a8.14 8.14 0 0 1 2.4 5.8c0 4.52-3.68 8.2-8.2 8.2Zm4.5-6.14c-.25-.13-1.46-.72-1.69-.8-.23-.09-.39-.13-.56.13-.16.25-.64.8-.79.97-.15.16-.29.19-.54.06-.25-.13-1.04-.38-1.98-1.22-.73-.65-1.22-1.46-1.37-1.71-.14-.25-.02-.39.11-.51.11-.11.25-.29.38-.44.13-.15.16-.25.25-.42.08-.16.04-.31-.02-.44-.06-.13-.55-1.33-.76-1.83-.2-.48-.4-.42-.55-.42h-.47c-.16 0-.42.06-.64.31s-.83.81-.83 1.98.85 2.29.97 2.45c.13.16 1.67 2.55 4.04 3.58 1.4.6 1.94.65 2.64.55.42-.06 1.29-.53 1.47-1.04.18-.51.18-.94.13-1.03-.05-.09-.19-.14-.44-.26Z" />
  </svg>
);

const InstagramLogo = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden>
    <rect x="3.5" y="3.5" width="17" height="17" rx="4.5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
  </svg>
);
